import { directSupabase as supabase } from '../../lib/supabaseClient';
import {
  fromDbAccount,
  toDbAccount,
  validateAccountInput,
  type DirectoryAccount,
  type DirectoryAccountInput,
} from './accounts.model';

const ACCOUNT_COLUMNS = '*';

function errorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  const row = error as { message?: string; details?: string; hint?: string; code?: string };
  if (row.code === '23503') return 'This account is used by invoices, bills, arrangements or job cards, so it cannot be deleted. Untick Active instead.';
  if (row.code === '42P01') return 'Accounts are not set up in the database yet. Apply the KSU account invoicing migration.';
  return row.message || row.details || row.hint || fallback;
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

export const accountsService = {
  async list(): Promise<DirectoryAccount[]> {
    const { data, error } = await supabase
      .from('directory_accounts')
      .select(ACCOUNT_COLUMNS)
      .order('company_name', { ascending: true });
    if (error) throw new Error(errorMessage(error, 'Failed to load accounts'));
    return (data || []).map(fromDbAccount);
  },

  async get(id: string): Promise<DirectoryAccount | null> {
    const { data, error } = await supabase
      .from('directory_accounts')
      .select(ACCOUNT_COLUMNS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(errorMessage(error, 'Failed to load the account'));
    return data ? fromDbAccount(data) : null;
  },

  async create(input: DirectoryAccountInput): Promise<DirectoryAccount> {
    const problem = validateAccountInput(input);
    if (problem) throw new Error(problem);
    const { data, error } = await supabase
      .from('directory_accounts')
      .insert({ ...toDbAccount(input), created_by: await currentUserId() })
      .select(ACCOUNT_COLUMNS)
      .single();
    if (error) throw new Error(errorMessage(error, 'Failed to create the account'));
    return fromDbAccount(data);
  },

  async update(id: string, input: DirectoryAccountInput): Promise<DirectoryAccount> {
    const problem = validateAccountInput(input);
    if (problem) throw new Error(problem);
    const { data, error } = await supabase
      .from('directory_accounts')
      .update(toDbAccount(input))
      .eq('id', id)
      .select(ACCOUNT_COLUMNS)
      .single();
    if (error) throw new Error(errorMessage(error, 'Failed to save the account'));
    return fromDbAccount(data);
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from('directory_accounts').delete().eq('id', id);
    if (error) throw new Error(errorMessage(error, 'Failed to delete the account'));
  },
};
